import { useEffect, useMemo, useRef } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AppNav } from '@/components/AppNav';
import { FeatureFlagsProvider } from '@/contexts/FeatureFlagsContext';
import { JoinPlaybackPrompt } from '@/components/JoinPlaybackPrompt';
import { LobbyNotice } from '@/components/LobbyNotice';
import { LoginPage } from '@/pages/Login';
import { RegisterPage } from '@/pages/Register';
import { HomePage } from '@/pages/Home';
import { ResultsPage } from '@/pages/Results';
import { AccountPage } from '@/pages/Account';
import { SubscribePage } from '@/pages/Subscribe';
import { BillingPage } from '@/pages/Billing';
import { BillingGate } from '@/components/BillingGate';
import { ScreenLimitModal } from '@/components/ScreenLimitModal';
import { Watch } from '@/pages/Watch';
import { AccessCodesPage } from '@/pages/admin/AccessCodes';
import { AdminRecruitersPage } from '@/pages/admin/Recruiters';
import { ConstantsPage } from '@/pages/admin/Constants';
import { ServiceHealthPage } from '@/pages/admin/ServiceHealth';
import { SportsHealthPage } from '@/pages/admin/SportsHealth';
import { SportsVodResolveCachePage } from '@/pages/admin/SportsVodResolveCache';
import { SpendingPage } from '@/pages/admin/Spending';
import { LogsPage } from '@/pages/admin/Logs';
import { FeatureFlagsAdminPage } from '@/pages/admin/FeatureFlags';
import { AdminLayout } from '@/pages/admin/AdminLayout';
import { Sports } from '@/pages/Sports';
import { SportsVodLibrary } from '@/pages/SportsVodLibrary';
import { LiveTV } from '@/pages/LiveTV';
import { WatchLiveTV } from '@/pages/WatchLiveTV';
import { WatchSports } from '@/pages/WatchSports';
import { WatchSportsVod } from '@/pages/WatchSportsVod';
import { ProtectedRoute } from '@/routes/ProtectedRoute';
import { AdminRoute } from '@/routes/AdminRoute';
import { RecruiterRoute } from '@/routes/RecruiterRoute';
import { RecruiterLayout } from '@/pages/recruitment/RecruiterLayout';
import { Earnings } from '@/pages/recruitment/Earnings';
import { Users as RecruitmentUsers } from '@/pages/recruitment/Users';
import { Plans as RecruitmentPlans } from '@/pages/recruitment/Plans';
import { Trials as RecruitmentTrials } from '@/pages/recruitment/Trials';
import { Docs as RecruitmentDocs } from '@/pages/recruitment/Docs';
import { useLobbyWs } from '@/hooks/useLobbyWs';
import type { NowPlayingRef } from '@/types/lobby';

function WatchMovieRoute(): JSX.Element {
  const { tmdbId } = useParams();
  const id = Number(tmdbId);
  if (!Number.isFinite(id) || id <= 0) return <Navigate to="/" replace />;
  return <Watch streamRef={{ type: 'movie', tmdbId: id }} />;
}

function WatchTvRoute(): JSX.Element {
  const { tmdbId, season, episode } = useParams();
  const id = Number(tmdbId);
  const s = Number(season);
  const e = Number(episode);
  if (![id, s, e].every((n) => Number.isFinite(n) && n > 0)) {
    return <Navigate to="/" replace />;
  }
  return <Watch streamRef={{ type: 'tv', tmdbId: id, season: s, episode: e }} />;
}

function WatchSportsRoute(): JSX.Element {
  const { eventId } = useParams();
  const id = String(eventId ?? '').trim();
  // SportsEvent ids are 16-char hex per the catalog dedupe scheme. Accept any
  // 1..64 char [a-z0-9-] slug as a forward-compatible relaxation.
  if (id.length === 0 || id.length > 64 || !/^[a-z0-9-]+$/i.test(id)) {
    return <Navigate to="/sports" replace />;
  }
  return <WatchSports eventId={id} />;
}

function WatchSportsVodRoute(): JSX.Element {
  const { id } = useParams();
  const value = String(id ?? '').trim();
  // SportsVodTitle.id is a Prisma cuid (lowercase alphanumerics) but a future
  // schema swap to cuid2/ulid would change the character set, so we relax to
  // the same `[a-z0-9-]{1..64}` shape WatchSportsRoute accepts.
  if (value.length === 0 || value.length > 64 || !/^[a-z0-9-]+$/i.test(value)) {
    return <Navigate to="/" replace />;
  }
  return <WatchSportsVod id={value} />;
}

// daddylive ids are bare numeric (the /watch.php?id=N); iptv-org ids are
// namespaced (`iptv:<iptv-org id>`). Mirrors the backend CHANNEL_ID_RE.
const LIVETV_ID_RE = /^(?:\d{1,12}|iptv:[A-Za-z0-9._@-]{1,80})$/;

function WatchLiveTvRoute(): JSX.Element {
  const { channelId } = useParams();
  const id = String(channelId ?? '').trim();
  if (!LIVETV_ID_RE.test(id)) {
    return <Navigate to="/live-tv" replace />;
  }
  return <WatchLiveTV channelId={id} />;
}

function WatchAnimeRoute(): JSX.Element {
  const { malId, episode, audio } = useParams();
  const id = Number(malId);
  const e = Number(episode);
  if (![id, e].every((n) => Number.isFinite(n) && n > 0)) {
    return <Navigate to="/" replace />;
  }
  if (audio !== 'sub' && audio !== 'dub') {
    return <Navigate to="/" replace />;
  }
  return <Watch streamRef={{ type: 'anime', malId: id, episode: e, audio }} />;
}
import { useAppDispatch, useAppSelector } from '@/store';
import { useAuth } from '@/hooks/useAuth';
import { useLocalProfile } from '@/hooks/useLocalProfile';
import { fetchCurrentUser, loginAsGuest } from '@/store/slices/authSlice';
import { mergeLocalHistoryIntoServer } from '@/store/slices/historySlice';
import { dismissJoinPrompt, getMyLobby, refsEqual } from '@/store/slices/lobbySlice';
import { fetchConstants } from '@/store/slices/constantsSlice';
import { posthog } from '@/observability/posthog';
import { Sentry } from '@/observability/sentry';

/** Build the `/watch/` URL for a lobby's current pick, including `?source=`. */
function nowPlayingToPath(ref: NowPlayingRef, source: string | null): string {
  const base = ref.kind === 'movie'
    ? `/watch/movie/${ref.tmdbId}`
    : ref.kind === 'tv'
    ? `/watch/tv/${ref.tmdbId}/${ref.season}/${ref.episode}`
    : ref.kind === 'anime'
    ? `/watch/anime/${ref.malId}/${ref.episode}/${ref.audio}`
    : `/watch/sports-vod/${encodeURIComponent(ref.sportsVodId)}`;
  return source ? `${base}?source=${encodeURIComponent(source)}` : base;
}

/** True iff the user is already on the `/watch/` URL for `ref` (ignoring query string). */
function pathnameMatchesRef(pathname: string, ref: NowPlayingRef): boolean {
  if (ref.kind === 'movie') return pathname === `/watch/movie/${ref.tmdbId}`;
  if (ref.kind === 'tv') return pathname === `/watch/tv/${ref.tmdbId}/${ref.season}/${ref.episode}`;
  if (ref.kind === 'anime') return pathname === `/watch/anime/${ref.malId}/${ref.episode}/${ref.audio}`;
  return pathname === `/watch/sports-vod/${ref.sportsVodId}`;
}

export function App(): JSX.Element {
  const dispatch = useAppDispatch();
  const { token, user, status, access, admin } = useAuth();
  const { localId, displayName } = useLocalProfile();
  const historySource = useAppSelector((s) => s.history.sourceOfTruth);
  const historyMerging = useAppSelector((s) => s.history.merging);

  // Admin-tunable constants are public and load before auth — they gate
  // player UX (next-episode threshold) and the admin Constants page.
  useEffect(() => {
    void dispatch(fetchConstants());
  }, [dispatch]);

  // Existing token in storage → resolve /me to hydrate the user.
  useEffect(() => {
    if (token && !user && status !== 'loading') {
      void dispatch(fetchCurrentUser());
    }
  }, [token, user, status, dispatch]);

  // No token + nothing in flight → mint a guest JWT so anonymous users can
  // immediately hit lobby and (post-redeem) the access-gated routes.
  useEffect(() => {
    if (!token && status === 'idle') {
      void dispatch(loginAsGuest({ localId, displayName }));
    }
  }, [token, status, localId, displayName, dispatch]);

  // Drain local watch history into the server when (and only when) the user
  // becomes a real, access-granted account AND we still hold a local cache.
  // The merge thunk is idempotent end-to-end: it POSTs each local row (server
  // upsert dedups) then refetches the canonical list.
  useEffect(() => {
    const isRealUser = status === 'authenticated' && user && user.guest !== true;
    if (isRealUser && access && historySource === 'local' && !historyMerging) {
      void dispatch(mergeLocalHistoryIntoServer());
    }
  }, [status, user, access, historySource, historyMerging, dispatch]);

  // Restore any persisted lobby for real, access-granted users on boot.
  // (Backend has no /api/lobby/me; the slice validates the persisted lobby
  // via GET /api/lobby/:id and clears it on 404.)
  const lobbyHydrationRanRef = useRef(false);
  useEffect(() => {
    const isRealUser = status === 'authenticated' && user && user.guest !== true;
    if (isRealUser && access && !lobbyHydrationRanRef.current) {
      lobbyHydrationRanRef.current = true;
      void dispatch(getMyLobby());
    }
    if (!isRealUser || !access) {
      lobbyHydrationRanRef.current = false;
    }
  }, [status, user, access, dispatch]);

  const location = useLocation();
  const navigate = useNavigate();
  const inLobby = useAppSelector((s) => s.lobby.currentLobby != null);
  const nowPlaying = useAppSelector((s) => s.lobby.nowPlaying);
  const dismissedRef = useAppSelector((s) => s.lobby.dismissedRef);

  // WS lifecycle is owned at the App level so `nowPlaying` populates regardless
  // of which route the user is on — required for the post-join prompt.
  useLobbyWs();

  // Whether the user is already on the lobby's current pick. When true we
  // suppress both the prompt and the AppNav "Join Lobby" button.
  const onLobbyContentRoute = useMemo(() => {
    if (!nowPlaying) return false;
    return pathnameMatchesRef(location.pathname, nowPlaying.ref);
  }, [location.pathname, nowPlaying]);

  // Show the "Join the watch party?" prompt when in a lobby with a pick AND
  // not on that pick's URL AND we haven't already declined this exact ref.
  const showJoinPrompt = inLobby
    && nowPlaying != null
    && !onLobbyContentRoute
    && !refsEqual(dismissedRef, nowPlaying.ref);

  // Persistent nav-bar "Join Lobby" CTA — visible whenever the lobby has a
  // pick that the user isn't currently on. Same condition as showJoinPrompt
  // minus the dismissed-ref filter (the button is the fallback for users who
  // declined the modal).
  const showJoinLobbyButton = inLobby
    && nowPlaying != null
    && !onLobbyContentRoute;

  function navigateToLobbyContent(): void {
    if (!nowPlaying) return;
    navigate(nowPlayingToPath(nowPlaying.ref, nowPlaying.source));
  }

  function onAcceptJoinPrompt(): void {
    navigateToLobbyContent();
  }

  function onDeclineJoinPrompt(): void {
    if (!nowPlaying) return;
    dispatch(dismissJoinPrompt(nowPlaying.ref));
  }

  // Identify real, access-granted users in PostHog + Sentry. Guests stay
  // anonymous (their JWT `sub` is per-browser and would fragment one human
  // across many anonymous IDs).
  useEffect(() => {
    const isRealUser = status === 'authenticated' && user && user.guest !== true;
    if (isRealUser && access) {
      posthog.identify(user.id, {
        email: user.email,
        displayName: user.displayName ?? null,
        access: true,
        admin,
      });
      Sentry.setUser({ id: user.id, email: user.email });
    }
  }, [status, user, access, admin]);

  // Reset on logout — triggers when the user transitions from set → null.
  const prevUserId = useRef<string | null>(null);
  useEffect(() => {
    if (prevUserId.current && !user) {
      posthog.reset();
      Sentry.setUser(null);
    }
    prevUserId.current = user?.id ?? null;
  }, [user]);

  return (
    <FeatureFlagsProvider>
    <div className="app-shell">
      <AppNav
        showJoinLobbyButton={showJoinLobbyButton}
        onJoinLobby={navigateToLobbyContent}
      />
      <LobbyNotice />
      <BillingGate />
      <ScreenLimitModal />
      {showJoinPrompt && nowPlaying && (
        <JoinPlaybackPrompt
          nowPlaying={nowPlaying}
          onAccept={onAcceptJoinPrompt}
          onDecline={onDeclineJoinPrompt}
        />
      )}
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/account" element={<AccountPage />} />
        {/* Legacy /profile still resolves to the Account tab (ProtectedRoute and
            older links point here, passing { needAccess } state we still read). */}
        <Route path="/profile" element={<AccountPage />} />
        <Route
          path="/subscribe"
          element={
            <ProtectedRoute>
              <SubscribePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account/billing"
          element={
            <ProtectedRoute>
              <BillingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/results"
          element={
            <ProtectedRoute requireAccess>
              <ResultsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/watch/movie/:tmdbId"
          element={
            <ProtectedRoute requireAccess>
              <WatchMovieRoute />
            </ProtectedRoute>
          }
        />
        <Route
          path="/watch/tv/:tmdbId/:season/:episode"
          element={
            <ProtectedRoute requireAccess>
              <WatchTvRoute />
            </ProtectedRoute>
          }
        />
        <Route
          path="/watch/anime/:malId/:episode/:audio"
          element={
            <ProtectedRoute requireAccess>
              <WatchAnimeRoute />
            </ProtectedRoute>
          }
        />
        <Route
          path="/sports"
          element={
            <ProtectedRoute requireAccess>
              <Sports />
            </ProtectedRoute>
          }
        />
        <Route
          path="/watch/sports/:eventId"
          element={
            <ProtectedRoute requireAccess>
              <WatchSportsRoute />
            </ProtectedRoute>
          }
        />
        <Route
          path="/sports-vod"
          element={
            <ProtectedRoute requireAccess>
              <SportsVodLibrary />
            </ProtectedRoute>
          }
        />
        <Route
          path="/watch/sports-vod/:id"
          element={
            <ProtectedRoute requireAccess>
              <WatchSportsVodRoute />
            </ProtectedRoute>
          }
        />
        <Route
          path="/live-tv"
          element={
            <ProtectedRoute requireAccess>
              <LiveTV />
            </ProtectedRoute>
          }
        />
        <Route
          path="/watch/live-tv/:channelId"
          element={
            <ProtectedRoute requireAccess>
              <WatchLiveTvRoute />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <Navigate to="/admin/access-codes" replace />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/access-codes"
          element={
            <AdminRoute>
              <AdminLayout>
                <AccessCodesPage />
              </AdminLayout>
            </AdminRoute>
          }
        />
        <Route
          path="/admin/recruiters"
          element={
            <AdminRoute>
              <AdminLayout>
                <AdminRecruitersPage />
              </AdminLayout>
            </AdminRoute>
          }
        />
        <Route
          path="/admin/constants"
          element={
            <AdminRoute>
              <AdminLayout>
                <ConstantsPage />
              </AdminLayout>
            </AdminRoute>
          }
        />
        <Route
          path="/admin/health"
          element={
            <AdminRoute>
              <AdminLayout>
                <ServiceHealthPage />
              </AdminLayout>
            </AdminRoute>
          }
        />
        <Route
          path="/admin/sports-health"
          element={
            <AdminRoute>
              <AdminLayout>
                <SportsHealthPage />
              </AdminLayout>
            </AdminRoute>
          }
        />
        <Route
          path="/admin/sports-vod-resolve-cache"
          element={
            <AdminRoute>
              <AdminLayout>
                <SportsVodResolveCachePage />
              </AdminLayout>
            </AdminRoute>
          }
        />
        <Route
          path="/admin/spending"
          element={
            <AdminRoute>
              <AdminLayout>
                <SpendingPage />
              </AdminLayout>
            </AdminRoute>
          }
        />
        <Route
          path="/admin/logs"
          element={
            <AdminRoute>
              <AdminLayout>
                <LogsPage />
              </AdminLayout>
            </AdminRoute>
          }
        />
        <Route
          path="/admin/feature-flags"
          element={
            <AdminRoute>
              <AdminLayout>
                <FeatureFlagsAdminPage />
              </AdminLayout>
            </AdminRoute>
          }
        />
        <Route path="/recruitment" element={<RecruiterRoute><Navigate to="/recruitment/earnings" replace /></RecruiterRoute>} />
        <Route path="/recruitment/earnings" element={<RecruiterRoute><RecruiterLayout><Earnings /></RecruiterLayout></RecruiterRoute>} />
        <Route path="/recruitment/users" element={<RecruiterRoute><RecruiterLayout><RecruitmentUsers /></RecruiterLayout></RecruiterRoute>} />
        <Route path="/recruitment/plans" element={<RecruiterRoute><RecruiterLayout><RecruitmentPlans /></RecruiterLayout></RecruiterRoute>} />
        <Route path="/recruitment/trials" element={<RecruiterRoute><RecruiterLayout><RecruitmentTrials /></RecruiterLayout></RecruiterRoute>} />
        <Route path="/recruitment/docs" element={<RecruiterRoute><RecruiterLayout><RecruitmentDocs /></RecruiterLayout></RecruiterRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
    </FeatureFlagsProvider>
  );
}
